import { useQuery } from '@tanstack/react-query';
import { getReservoir } from '../api/golden';
import {
  getAchievements,
  getDrugCardUnlocks,
  getDrugCardAchievementProgress,
} from '../api/achievements';

/**
 * Player-state query hooks.
 *
 * Single source of truth for player-facing server state. Replaces the
 * scatter of direct `getReservoir()` / `getAchievements()` calls across
 * AchievementsOrb / PlayerShop / PlayerStats / BottomLeftStatusRail —
 * each of which used to fire its own request with no caching or
 * deduplication. React Query handles:
 *
 *   - Request deduplication: N hooks with the same queryKey = 1 fetch
 *   - Stale-while-revalidate: instant render from cache, background refresh
 *   - Invalidation API: call `useQueryClient().invalidateQueries(playerQueryKeys.reservoir)`
 *     after a purchase / unlock / admin grant
 *   - Devtools: hooks show up by name in the React Query devtools panel,
 *     making "why is this component re-rendering" debuggable
 *
 * Convention for adding a new hook:
 *   1. Add an entry to `playerQueryKeys` (keep names stable — they're
 *      the invalidation contract)
 *   2. Add a `useXxx` hook below that calls `useQuery` with the key + fn
 *   3. Export the API function from `frontend/src/api/*` (don't inline
 *      fetch logic in the hook — the hook is a thin caching wrapper)
 */
export const playerQueryKeys = {
  reservoir: ['player', 'reservoir'],
  drugCardUnlocks: ['player', 'drug-card-unlocks'],
  drugCardAchievementProgress: ['player', 'drug-card-achievement-progress'],
  legacyAchievements: ['player', 'legacy-achievements'],
};

/**
 * Golden-card reservoir with per-user enrichment.
 * Returns the array of card entries from /api/golden/reservoir.
 */
export function useReservoir(options = {}) {
  return useQuery({
    queryKey: playerQueryKeys.reservoir,
    queryFn: getReservoir,
    placeholderData: (prev) => prev ?? [],
    ...options,
  });
}

/**
 * Per-user drug-card unlock rows (new substrate — drug_card_unlocks).
 * Empty array while substrate is dormant. Populated by keystone_genesis,
 * admin_grant, or currency_purchase unlock paths.
 */
export function useDrugCardUnlocks(options = {}) {
  return useQuery({
    queryKey: playerQueryKeys.drugCardUnlocks,
    queryFn: getDrugCardUnlocks,
    placeholderData: (prev) => prev ?? [],
    ...options,
  });
}

/**
 * Per-user drug-card achievement progress (criterion counters).
 * Each row is one (card_slug, criterion_key) with current_value/target_value.
 */
export function useDrugCardAchievementProgress(options = {}) {
  return useQuery({
    queryKey: playerQueryKeys.drugCardAchievementProgress,
    queryFn: getDrugCardAchievementProgress,
    placeholderData: (prev) => prev ?? [],
    ...options,
  });
}

/**
 * Legacy `user_achievements` rows + definitions. Used by surfaces that
 * still cross-reference legacy achievement IDs (the gold card shop's
 * `required_achievement` gating, the BLS rail's recent-earn summary).
 * Will be retired once all cards migrate to criterion gates and the
 * legacy roster is deleted (per CP-5f-v3 achievements subspec Phase E).
 */
export function useLegacyAchievements(options = {}) {
  return useQuery({
    queryKey: playerQueryKeys.legacyAchievements,
    queryFn: getAchievements,
    placeholderData: (prev) => prev ?? [],
    ...options,
  });
}
